/**
 * @file Derives the initial beneficiary selection and custom amounts from saved distributions.
 */
import type { ScheduleDistribution } from "@api/types/transactions/types.gen";

import type { BeneficiaryItem, Frequency } from "../../types";
import getDefaultAmount from "./getDefaultAmount";

/**
 * Finds the saved distribution for a beneficiary row, if one exists.
 */
export const matchDistributionToItem = (
  item: BeneficiaryItem,
  distributions: ScheduleDistribution[],
): ScheduleDistribution | undefined =>
  distributions.find(
    ({ investmentId, tierId, investorAccountId }) =>
      investmentId === item.investmentId &&
      tierId === item.tierId &&
      investorAccountId === item.investorAccountId,
  );

/**
 * Builds the initial selection and the custom amounts that differ from the default.
 */
export const deriveBeneficiaryInitFromDistributions = (
  items: BeneficiaryItem[],
  initialDistributions: ScheduleDistribution[],
  distributionFrequency: Frequency,
  shouldRoundCents: boolean,
): { selectedKeys: string[]; customAmountByKey: Record<string, number> } => {
  const selectedKeys: string[] = [];
  const customAmountByKey: Record<string, number> = {};

  for (const item of items) {
    const dist = matchDistributionToItem(item, initialDistributions);

    if (dist) {
      selectedKeys.push(item.key);

      const defaultAmount = getDefaultAmount(item, distributionFrequency, shouldRoundCents);
      const savedCents = Math.round(Number(dist.amount) || 0);

      if (savedCents !== defaultAmount) {
        customAmountByKey[item.key] = savedCents;
      }
    }
  }

  return { selectedKeys, customAmountByKey };
};
